import React, { useEffect, useState } from 'react';
import { useInvalidateLineRead, useLineActionCall, useLineRead, type Obj } from '@/client/api/whatsapp/line-actions';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { ActionButton, ConfirmDialog, Empty, ErrorNote, Loading, selectCls, toastError } from './shared';

const WHO: [string, string][] = [['all', 'Everyone'], ['contacts', 'My contacts'], ['contact_blacklist', 'My contacts, except...'], ['none', 'Nobody']];
const FIELDS: { key: string; label: string; options: [string, string][] }[] = [
  { key: 'readreceipts', label: 'Read receipts', options: [['all', 'Send read receipts'], ['none', 'Do not send']] },
  { key: 'profile', label: 'Profile photo', options: WHO },
  { key: 'status', label: 'About / status', options: WHO },
  { key: 'online', label: 'Who can see when online', options: [['all', 'Everyone'], ['match_last_seen', 'Same as last seen']] },
  { key: 'last', label: 'Last seen', options: WHO },
  { key: 'groupadd', label: 'Who can add to groups', options: WHO },
];

const PrivacyTab: React.FC<{ instance: string; connected: boolean }> = ({ instance, connected }) => {
  const q = useLineRead<Obj>(instance, 'chat.fetchPrivacySettings', { enabled: connected });
  const call = useLineActionCall();
  const invalidate = useInvalidateLineRead();
  const [form, setForm] = useState<Obj | null>(null);
  const [ask, setAsk] = useState(false);
  useEffect(() => {
    if (q.data) setForm(Object.fromEntries(FIELDS.map((f) => [f.key, q.data![f.key] ?? f.options[0][0]])));
  }, [q.data]);

  if (!connected) return <Empty title="No phone is linked" hint="Link a phone from the Overview tab to manage its privacy settings." />;
  const dirty = !!form && !!q.data && FIELDS.some((f) => form[f.key] !== q.data![f.key]);

  return (
    <div className="space-y-4">
      {q.isLoading && <Loading what="privacy settings" />}
      {q.isError && <ErrorNote error={q.error} what="privacy settings" />}
      {form && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <select className={selectCls} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                  {f.options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </div>
            ))}
          </div>
          <ActionButton action="chat.updatePrivacySettings" disabled={!dirty} onClick={() => setAsk(true)}>Save privacy settings</ActionButton>
        </>
      )}
      <ConfirmDialog
        open={ask}
        title="Change privacy settings?"
        body="These are the WhatsApp privacy settings of the linked phone. They apply to the phone's account right away."
        action="Save"
        pending={call.isPending}
        onClose={() => setAsk(false)}
        onConfirm={() =>
          call.mutate(
            { instance, action: 'chat.updatePrivacySettings', body: form ?? {} },
            {
              onSuccess: () => { toast.success('Privacy settings saved'); setAsk(false); invalidate(instance, 'chat.fetchPrivacySettings'); },
              onError: (e) => { setAsk(false); toastError(e, 'Could not save the privacy settings'); },
            },
          )
        }
      />
    </div>
  );
};

export default PrivacyTab;
